/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — «UI nuova» Escape stack (M11 W1, pattern P5): Escape
 *              closes the TOPMOST layer only. Two halves: (a) the OPEN ORDER of
 *              the stacking overlays (BbModal, BbModalModern, NvSheet push on
 *              open, pop on close) — the top is the last opened, whatever its
 *              DOM position; (b) a DOM check for the page under every overlay
 *              (a record ✕ must not close while any layer, menus included, is
 *              shown). isOpenLayer is the ONE visibility rule for both.
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      DOM only (document.documentElement[data-ui], querySelectorAll,
 *              getComputedStyle)
 *   siblings:  lib/nv-layer-history.js (Back closes the top entry via its `close`,
 *              setStackListener), components/bb-modal.js (escapeKey), components/bb-modal-modern.js
 *              (handleKeydown + document listener); beebee-base-template
 *              lib/nv-layers.js (re-exports this), nv-sheet.js, bb-detail-button.js,
 *              bb-main-menu-nuova.js
 *   tests:     medical tests/integration/components/nv-sheet («Escape» tests —
 *              the real ember-keyboard on-key is app-only)
 *   gotchas:   (1) classic (no html[data-ui='nuova']) never reads the stack: the
 *              callers keep their old path; pushing/popping is bookkeeping only.
 *              (2) Order by DOM position is wrong: BbModalModern renders at the
 *              end of <body> and can open an app-level inline BbModal that sits
 *              EARLIER in the document — hence the open-order stack. (3) A
 *              closed BbDropdown pane stays in the DOM (display:flex, opacity 0,
 *              role=menu), the calendar keeps role=menu panes mounted: they are
 *              not open layers (isOpenLayer).
 */

// Shown overlays that own the Escape key before the page under them.
const NV_LAYER_SELECTOR = ['[aria-modal="true"]', '[role="menu"]', '.beebee-modal-modern-overlay.show', '[data-nv-layer]',
// an open BbDropdown pane (e.g. «Registra incasso» from the invoice money card, M11 live fix 13): BbDropdown
// closes itself on Escape; the record under it must not
'.dropdown-pane.is-open'].join(', ');

/** True when the «UI nuova» is on (html[data-ui='nuova'], set by service:ui). */
function isNuovaDocument() {
  return document.documentElement?.dataset?.ui === 'nuova';
}

/**
 * True when `el` is an overlay actually open on screen: laid out (client rects), not a closed BbDropdown pane,
 * not inside aria-hidden="true", not visibility:hidden, not fully transparent.
 */
function isOpenLayer(el) {
  if (!el || typeof el.getClientRects !== 'function' || el.getClientRects().length === 0) return false;
  if (el.classList?.contains('dropdown-pane') && !el.classList.contains('is-open')) return false;
  // an `is-open` pane is open from the first frame: its opacity fades in from 0 (custom_new.scss transition), so
  // the opacity rule below would call it closed for that fade
  if (el.classList?.contains('dropdown-pane') && !el.closest?.('[aria-hidden="true"]')) return true;
  if (el.closest?.('[aria-hidden="true"]')) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.opacity === '0') return false;
  return true;
}

/**
 * True when an open layer that does not contain `element` is on screen: the Escape key belongs to that layer,
 * not to `element` (the record under every overlay). A layer never blocks what it contains.
 */
function hasOpenLayerAbove(element) {
  for (const layer of document.querySelectorAll(NV_LAYER_SELECTOR)) {
    if (element && (layer === element || layer.contains(element))) continue;
    if (isOpenLayer(layer)) return true;
  }
  return false;
}

/**
 * True when an open layer sits INSIDE `root` (a modal): an open BbDropdown pane or a `[data-nv-layer]` descendant
 * (lead review 5, e.g. the icon picker of the payment-method modal), or an open nuova combobox list rendered in the
 * body wormhole (`[data-nv-cb]`, SelectAssociation). The modal leaves Escape to it (the inverse of
 * hasOpenLayerAbove, which skips the layers that contain the element).
 */
function hasOpenLayerInside(root) {
  if (!root?.querySelectorAll) return false;
  for (const el of root.querySelectorAll('.dropdown-pane.is-open, [data-nv-layer]')) {
    if (el !== root && isOpenLayer(el)) return true;
  }
  for (const el of document.querySelectorAll('[data-nv-cb]')) {
    if (!root.contains(el) && isOpenLayer(el)) return true;
  }
  return false;
}

// ── open order ───────────────────────────────────────────────────────────────────────────────────────────────────
// Entries { token, element }. A parent and a child opened in the SAME render install their modifiers child first:
// an overlay that contains an already-stacked one goes BELOW it, so the nested one stays on top.
const STACK = [];

// One observer of the stack (lib/nv-layer-history: the Back button). Called after every push / remove.
let stackListener = null;

/** Registers the stack observer (null clears it). */
function setStackListener(fn) {
  stackListener = typeof fn === 'function' ? fn : null;
}
function notifyStackChanged() {
  stackListener?.();
}

/**
 * An overlay opened (moves to the top if already there). `element`: its root, for the nesting rule.
 * `close`: how the Back button closes it — the overlay's own close path (busy / dirty guards included).
 */
function pushLayer(token, element = null, close = null) {
  removeEntry(token);
  const entry = {
    token,
    element,
    close: typeof close === 'function' ? close : null
  };
  const nestedAt = element ? STACK.findIndex(other => other.element && element.contains(other.element)) : -1;
  if (nestedAt >= 0) {
    STACK.splice(nestedAt, 0, entry);
  } else {
    STACK.push(entry);
  }
  notifyStackChanged();
}
function removeEntry(token) {
  const index = STACK.findIndex(entry => entry.token === token);
  if (index < 0) return false;
  STACK.splice(index, 1);
  return true;
}

/** An overlay closed / torn down. */
function removeLayer(token) {
  if (removeEntry(token)) notifyStackChanged();
}

/** The open overlays' tokens, bottom first (a copy). */
function stackedTokens() {
  return STACK.map(entry => entry.token);
}

/** The last opened overlay still open: `{ token, element, close }`, or null. */
function topLayer() {
  return STACK.length > 0 ? STACK[STACK.length - 1] : null;
}

/** True when `token` is the last opened overlay still open. */
function isTopLayer(token) {
  return STACK.length > 0 && STACK[STACK.length - 1].token === token;
}

/** True when any stacking overlay (modal, sheet) is open. */
function hasStackedLayers() {
  return STACK.length > 0;
}

export { NV_LAYER_SELECTOR, hasOpenLayerAbove, hasOpenLayerInside, hasStackedLayers, isNuovaDocument, isOpenLayer, isTopLayer, pushLayer, removeLayer, setStackListener, stackedTokens, topLayer };
//# sourceMappingURL=nv-layer-stack.js.map
